import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { ClockwiseIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'

import useAuth from '../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { HostFamilyHistory } from '../../../../lib/types/models/host-family-history/host-family-history'
import LabelWithText from '../../ols/label-with-text'

type Props = {
  hostFamilyHistory?: (Pick<
    HostFamilyHistory,
    'reasonForChange' | 'diffusedToParentsAt' | 'date'
  > & {
    hostFamily: Pick<
      HostFamilyHistory['hostFamily'],
      'id' | 'parent1FirstName' | 'parent1LastName'
    >
  })[]
}

const HostFamilyHistoryItem = ({
  history
}: {
  history: Pick<HostFamilyHistory, 'reasonForChange' | 'diffusedToParentsAt' | 'date'> & {
    hostFamily: Pick<
      HostFamilyHistory['hostFamily'],
      'id' | 'parent1FirstName' | 'parent1LastName'
    >
  }
}) => {
  const { t } = useTranslate()
  const { userRole } = useAuth()

  return (
    <div className={cn('flex flex-col gap-2', MODAL_CONTENT_CLASSNAME)}>
      <div className="flex items-center justify-between">
        <div className="w-1/2 items-start flex flex-col gap-1">
          <LabelWithText
            display="row"
            className="gap-1"
            label={`${t('ol.center.host.family.placement.history.hostFamily.name')} :`}
            data={
              <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                {toFullName(
                  history.hostFamily.parent1FirstName,
                  history.hostFamily.parent1LastName,
                  { options: { mode: 'lastname-firstname' } }
                )}
              </Typography>
            }
          />

          {history.reasonForChange && (
            <LabelWithText
              display="row"
              className="text-nowrap gap-1"
              label={`${t('ol.center.host.family.placement.history.reason')} :`}
              data={
                <Typography
                  variant="label-semibold"
                  color={tailwindColors.grey.DEFAULT}
                  className="text-nowrap"
                >
                  {history.reasonForChange}
                </Typography>
              }
            />
          )}

          {userRole === RoleEnum.ADMIN && !!history.diffusedToParentsAt && (
            <LabelWithText
              display="row"
              className="text-nowrap gap-1"
              label={`Diffusé au parents ?`}
              data={
                <Typography
                  variant="label-semibold"
                  color={tailwindColors.grey.DEFAULT}
                  className="text-nowrap"
                >
                  Diffusé le {DateFormatter.DDMMYYYY(history.diffusedToParentsAt)}
                </Typography>
              }
            />
          )}
        </div>
        <Badge variant="white" text={DateFormatter.DDMMYYYY(history.date)} />
      </div>
    </div>
  )
}

const OlCenterHostFamilyPlacementHistory = ({ hostFamilyHistory }: Props) => {
  const { t } = useTranslate()
  const { modalState, toggleModal, closeModel } = useModalManager<'view-history'>(['view-history'])

  return (
    <>
      <Badge
        iconPosition="start"
        icon={<ClockwiseIcon color={tailwindColors.primary.DEFAULT} />}
        text={hostFamilyHistory?.length}
        onClick={() => toggleModal('view-history')}
        borderColor={tailwindColors.grey.light.DEFAULT}
        className="h-6 "
        iconClassName="m-0"
        size="xsmall"
      />
      <Modal
        isOpen={modalState['view-history']}
        title={t('ol.center.host.family.placement.history.title')}
        icon={ClockwiseIcon}
        hasBorderBottom
        actionButtonsOptions={{
          cancelText: t('button.close')
        }}
        size="medium"
        onCancel={() => closeModel('view-history')}
      >
        {hostFamilyHistory && hostFamilyHistory.length > 0 && (
          <div className="flex flex-col divide-y divide-white-active space-y-1">
            {hostFamilyHistory.map((history, index) => (
              <HostFamilyHistoryItem key={index} history={history} />
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}

export default OlCenterHostFamilyPlacementHistory
